import React from 'react';
import { camelotColor } from '../utils/harmonic';
import './CamelotWheel.scss';

// Musical key for each Camelot slot, in the spelling the Library filter uses.
// A = minor (inner ring), B = major (outer ring).
const A_MINOR = { 1: 'G#m', 2: 'D#m', 3: 'A#m', 4: 'Fm', 5: 'Cm', 6: 'Gm', 7: 'Dm', 8: 'Am', 9: 'Em', 10: 'Bm', 11: 'F#m', 12: 'C#m' };
const B_MAJOR = { 1: 'B', 2: 'F#', 3: 'C#', 4: 'G#', 5: 'D#', 6: 'A#', 7: 'F', 8: 'C', 9: 'G', 10: 'D', 11: 'A', 12: 'E' };

const SIZE = 250;
const C = SIZE / 2;
const OUTER = 120;
const MID = 78;
const HOLE = 42;

// Point on a circle at `ang` degrees, measured clockwise from the top.
const polar = (r, ang) => {
    const t = (ang * Math.PI) / 180;
    return [C + r * Math.sin(t), C - r * Math.cos(t)];
};

// Annular sector (donut slice) path from a1→a2 (clockwise-from-top degrees).
const sector = (ri, ro, a1, a2) => {
    const [x1o, y1o] = polar(ro, a1);
    const [x2o, y2o] = polar(ro, a2);
    const [x2i, y2i] = polar(ri, a2);
    const [x1i, y1i] = polar(ri, a1);
    return `M ${x1o} ${y1o} A ${ro} ${ro} 0 0 1 ${x2o} ${y2o} L ${x2i} ${y2i} A ${ri} ${ri} 0 0 0 ${x1i} ${y1i} Z`;
};

// Interactive Camelot wheel used as the Library "Keys" filter.
//   selectedKeys : musical key strings currently selected (e.g. ['Am','C'])
//   onToggle     : toggle a musical key
//   notation     : 'camelot' → label segments 8A/8B, 'musical' → Am/C
const CamelotWheel = ({ selectedKeys = [], onToggle, notation = 'camelot' }) => {
    const anySelected = selectedKeys.length > 0;
    const segs = [];

    for (let n = 1; n <= 12; n++) {
        const center = (n * 30) % 360; // 12 at top, 1 clockwise, …
        const a1 = center - 15;
        const a2 = center + 15;
        const rings = [
            { letter: 'B', mkey: B_MAJOR[n], ri: MID, ro: OUTER, lr: (MID + OUTER) / 2, fs: 11 },
            { letter: 'A', mkey: A_MINOR[n], ri: HOLE, ro: MID, lr: (HOLE + MID) / 2, fs: 9 },
        ];
        rings.forEach(({ letter, mkey, ri, ro, lr, fs }) => {
            const code = `${n}${letter}`;
            const sel = selectedKeys.includes(mkey);
            const active = sel || !anySelected;
            const [lx, ly] = polar(lr, center);
            const label = notation === 'musical' ? mkey : code;
            segs.push(
                <g
                    key={code}
                    className="camelot-seg"
                    onClick={() => onToggle && onToggle(mkey)}
                    role="button"
                    aria-pressed={sel}
                >
                    <title>{`${code} · ${mkey}`}</title>
                    <path
                        d={sector(ri, ro, a1, a2)}
                        fill={camelotColor(mkey)}
                        stroke={sel ? '#ffffff' : 'rgba(0,0,0,0.28)'}
                        strokeWidth={sel ? 2 : 0.75}
                        opacity={active ? 1 : 0.28}
                    />
                    <text
                        x={lx}
                        y={ly}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={fs}
                        fontWeight="700"
                        fill="#1a1a1a"
                        opacity={active ? 1 : 0.5}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                        {label}
                    </text>
                </g>
            );
        });
    }

    return (
        // Fixed pixel size (not width:100%) so the wheel never balloons when the
        // filtered track list empties and the surrounding panel reflows wider.
        <svg
            className="camelot-wheel"
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            role="group"
            aria-label="Key filter"
            style={{ width: '100%', maxWidth: `${SIZE}px`, height: 'auto', display: 'block' }}
        >
            {segs}
            <circle cx={C} cy={C} r={HOLE - 2} fill="var(--color-panel-bg)" />
            <text x={C} y={anySelected ? C - 9 : C} textAnchor="middle" dominantBaseline="central" fontSize="11" fontWeight="600" fill="var(--color-text-secondary)">Keys</text>
            {anySelected && (
                <text
                    x={C}
                    y={C + 10}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="12"
                    fontWeight="600"
                    fill="var(--color-accent)"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); selectedKeys.forEach((k) => onToggle && onToggle(k)); }}
                >
                    clear
                </text>
            )}
        </svg>
    );
};

export default CamelotWheel;
